import { labels } from '@/app/constants/labels';
import Chip from '@ixigo/iui/Chip';
import Collapse from '@ixigo/iui/Collapse';
import Radio from '@ixigo/iui/Radio';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import EmiPlanSelection from '../../EMISection/EmiPlanSelection';
import PaymentOptionOffer from '../../PaymentOptionOffer';
import CardDOTPBottomsheet from '../DOTPPanel/CardDOTPBottomsheet';
import CardBrandLogo from './CardBrandLogo';
import SavedCardForm from './SavedCardForm';
import type { SavedCardProps } from './types';
import useSavedCard from './useSavedCard';

const SavedCardWithEMI = ({ card, className }: SavedCardProps) => {
	const {
		savedCardCVV,
		onCardCVVChange,
		cvvError,
		isCvvLessSupported,
		onPayClick,
		loading,
		isDisabled,
		isSelected,
		setSelectedPaymentOption,
		cardName,
		cardSubtext,
		bankLogo,
		hasNoCostEmi,
		downtimeText,
		emiOptions,
		cardEmiOptions,
		cardBrand,
		offers,
		selectedEmiPlan,
		setSelectedEmiPlan,
		cardDOTPData,
		onCloseDOTP,
		dotpGatewayError,
	} = useSavedCard(card, true);

	const formProps = {
		card,
		isCvvLessSupported,
		onCardCVVChange,
		savedCardCVV,
		cvvError,
		onPayClick,
		loading,
		isSelected,
		ctaText: labels.PAY_SECURELY_TEXT,
	};
	return (
		<div
			className={clsx(
				'cursor-pointer',
				className,
				isDisabled ? 'opacity-50 pointer-events-none' : '',
			)}
		>
			<div className="flex items-center justify-between gap-10 relative">
				<div className="flex items-center gap-10">
					<img className="h-40 w-40 bg-contain bg-no-repeat" src={bankLogo} alt={cardName} />
					<div>
						<Typography
							variant="body-md"
							fontWeight="medium"
							className="flex items-center gap-2 flex-wrap"
						>
							{cardName}
							{hasNoCostEmi ? (
								<Chip size="sm" color="success" as="span">
									{labels.NO_COST_EMI_TEXT}
								</Chip>
							) : (
								''
							)}
						</Typography>
						<Typography variant="body-xs" className="flex items-center gap-1 text-charcoal-600">
							{cardSubtext}
							<CardBrandLogo cardBrand={cardBrand} />
						</Typography>
					</div>
				</div>

				<Radio
					onChange={() => (isDisabled ? undefined : setSelectedPaymentOption(card))}
					checked={isSelected}
					value={card.cardIssuer}
					overlay
				/>
			</div>
			<Collapse in={isSelected}>
				{cardEmiOptions && selectedEmiPlan && (
					<EmiPlanSelection
						emiPlans={emiOptions}
						selectedEmiPlan={selectedEmiPlan}
						onSelectEmiPlan={setSelectedEmiPlan}
						className="mt-10"
						bankCode={cardEmiOptions.bankCode}
					>
						<SavedCardForm {...formProps} />
					</EmiPlanSelection>
				)}
			</Collapse>
			{offers.length > 0 && !downtimeText && <PaymentOptionOffer offerText={offers[0]} />}
			{cardDOTPData && (
				<CardDOTPBottomsheet
					open={cardDOTPData !== null}
					onClose={onCloseDOTP}
					dotpData={cardDOTPData}
					ctaText={labels.DOTP_CTA_TEXT}
					gatewayError={dotpGatewayError}
				/>
			)}
		</div>
	);
};

export default SavedCardWithEMI;
